/**
 * The changeset board's FILE-LEVEL rules — which files a reader can open, in what order, and how
 * each one is described.
 *
 * ── WHY THESE ARE HERE AND NOT IN THE PANE ───────────────────────────────────────────────────
 *
 * `ChangesetPane` renders under `renderToStaticMarkup` in the repo's node (no-DOM) vitest
 * environment, and its detail view is behind `React.lazy` so Monaco stays out of that import
 * graph. Ordering, adjacency and the created/deleted/modified reading are the parts that are
 * wrong in ways a screenshot does not show — a "next file" that skips one, or a created file
 * described as a comparison against a baseline that does not exist — so they live here as pure
 * functions the node suite can assert directly.
 *
 * ONE ORDER, DEFINED ONCE. The list, the `‹ 2 of 6 ›` counter and the prev/next controls all read
 * `changedFiles`, so they cannot disagree about what "the next file" is. That ordering is the
 * manifest's own: components in `changeset.components` order, each component's files in its own
 * `files[]` order. Nothing is sorted here — the backend's order is the plan's order, and
 * resequencing it client-side would put the files in an order no other surface uses.
 */
import type { Changeset, Component, File as ChangesetFile } from "./protocol";
import { proposedWriteComponents } from "./changesetProvenance";

/** One openable file, with the component it belongs to (the detail header names both). */
export interface ChangedFileRef {
  file: ChangesetFile;
  component: Component;
}

/**
 * How a file's change reads to a person.
 *
 * `modified` is the only one that is genuinely a COMPARISON. A created file has no `before`
 * (`File.before` is null when status is `created`) and a deleted one has no `after`, so calling
 * either a diff and rendering it against an empty side is how a reader ends up asking "am I
 * looking at the original or the new one?" — the question this whole surface exists to stop them
 * having to ask. `DiffKind` is what lets the header say "New file" and mean it.
 */
export type DiffKind = "created" | "deleted" | "modified";

export function diffKind(file: Pick<ChangesetFile, "status">): DiffKind {
  if (file.status === "created") return "created";
  if (file.status === "deleted") return "deleted";
  return "modified";
}

export interface FileStatusMeta {
  /** The chip's word. Sentence case, because it is a label and not a shout. */
  label: string;
  /** What the detail header says this view IS, so the reader never has to infer it. */
  headline: string;
  /**
   * Token class for the chip.
   *
   * BORDER AND TEXT ONLY, NO FILL — the same shape (and the same reason) as the component rail's
   * own `BADGE_CLASS` in `ChangesetPane`: a soft tint under these words lightens their ground
   * enough to drop 10px text under AA, on exactly the words that distinguish a create from a
   * delete. Colour is never the only carrier here either; `label` says it.
   */
  tone: string;
}

/**
 * The status vocabulary, in ONE place.
 *
 * It was a bare `Record<status, string>` private to the pane holding "new"/"modified"/"deleted",
 * which is enough for a row and not enough for a header: a reader who opens `created` needs to be
 * told there is nothing to compare against, or the all-green editor reads as a broken diff.
 */
export const FILE_STATUS: Record<ChangesetFile["status"], FileStatusMeta> = {
  created: {
    label: "New",
    headline: "New file — every line is an addition, there is no earlier version to compare against.",
    tone: "border-ma-ok-line text-[var(--ma-state-ok-text)]",
  },
  modified: {
    label: "Modified",
    headline: "Modified — the left side is the current org version, the right side is proposed.",
    tone: "border-ma-warn-line text-[var(--ma-state-warn-text)]",
  },
  deleted: {
    label: "Deleted",
    headline: "Deleted — this file's current content is shown; the change removes it.",
    tone: "border-ma-err-line text-[var(--ma-state-err-text)]",
  },
  unchanged: {
    label: "Unchanged",
    headline: "Unchanged — this file is carried by the component but nothing in it moved.",
    tone: "border-border text-muted-foreground",
  },
};

/**
 * Every file a reader can OPEN, in manifest order.
 *
 * Scoped to `proposedWriteComponents` for the same reason every count and every write on this
 * board already is (rule 21): `changeset.components` also carries `user_branch`/`prior_turn` rows
 * the run was merely seeded with, and those are context, not proposed changes. Listing them as
 * openable files would offer to review something this change is not going to write.
 *
 * `unchanged` files are excluded because there is nothing to show: the backend answers `diff:
 * null` for one, honestly, and a row that opens an empty detail view is worse than a row that
 * explains itself in place (the list still renders them, inert, with the reason).
 */
export function changedFiles(changeset: Changeset): ChangedFileRef[] {
  const out: ChangedFileRef[] = [];
  for (const component of proposedWriteComponents(changeset.components)) {
    for (const file of component.files ?? []) {
      if (file.status === "unchanged") continue;
      out.push({ file, component });
    }
  }
  return out;
}

/** The openable file with this id, or `null` — which is also the answer when a newer changeset
 *  version has dropped the file the reader had open, and is why the pane falls back to the list
 *  rather than rendering a detail view for something that is no longer in the manifest. */
export function findChangedFile(changeset: Changeset, fileId: string | null | undefined): ChangedFileRef | null {
  if (!fileId) return null;
  return changedFiles(changeset).find((r) => r.file.file_id === fileId) ?? null;
}

/** Where this file sits in the openable list — `{ index: 1, total: 6 }` is "1 of 6", 1-based for
 *  display. `null` when the file is not in the list at all. */
export function filePosition(refs: readonly ChangedFileRef[], fileId: string): { index: number; total: number } | null {
  const at = refs.findIndex((r) => r.file.file_id === fileId);
  if (at < 0) return null;
  return { index: at + 1, total: refs.length };
}

/**
 * The next (`+1`) or previous (`-1`) file's id, or `null` at the end.
 *
 * DELIBERATELY NOT CIRCULAR. Wrapping from the last file back to the first is how a reader loses
 * their place in a six-file review: the control keeps working, so nothing says they have reached
 * the end, and they arrive back at file 1 believing it is file 7. `null` lets the caller disable
 * the control, which is the honest signal.
 */
export function adjacentFileId(
  refs: readonly ChangedFileRef[],
  fileId: string,
  delta: 1 | -1,
): string | null {
  const at = refs.findIndex((r) => r.file.file_id === fileId);
  if (at < 0) return null;
  const next = at + delta;
  if (next < 0 || next >= refs.length) return null;
  return refs[next].file.file_id;
}

/**
 * The last path segment — what a reader actually scans for in a list of
 * `force-app/main/default/classes/Foo.cls` paths.
 *
 * The full path is never DROPPED: every row keeps it as the `title` and the detail header renders
 * it in full. This is the leading line, not a replacement for it.
 */
export function fileName(path: string): string {
  const at = path.lastIndexOf("/");
  return at < 0 ? path : path.slice(at + 1);
}

/** The directory part, or `""` for a bare filename. Rendered dimmed BEFORE the name so the
 *  reader gets the name at a fixed position down the list instead of chasing it along a
 *  right-truncating path. */
export function fileDir(path: string): string {
  const at = path.lastIndexOf("/");
  return at < 0 ? "" : path.slice(0, at);
}
